import {AnimatePresence, animate} from '@sail/engine';
import {Button, FocusScope, view} from '@sail/ui';
import {FormattedMessage, MessageDescriptor} from 'react-intl';

type Props = {
  title: MessageDescriptor | string;
  description: MessageDescriptor | string;
  confirmLabel: MessageDescriptor | string;
  cancelLabel: MessageDescriptor | string;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function FullDrawerConfirmation({
  title,
  description,
  confirmLabel,
  cancelLabel,
  open,
  onConfirm,
  onCancel,
}: Props) {
  let titleMessage;
  if (typeof title === 'string') {
    titleMessage = title;
  } else {
    titleMessage = <FormattedMessage {...title} />;
  }

  let descriptionMessage;
  if (typeof description === 'string') {
    descriptionMessage = description;
  } else {
    descriptionMessage = <FormattedMessage {...description} />;
  }

  let cancelLabelMessage;
  if (typeof cancelLabel === 'string') {
    cancelLabelMessage = cancelLabel;
  } else {
    cancelLabelMessage = <FormattedMessage {...cancelLabel} />;
  }

  let confirmLabelMessage;
  if (typeof confirmLabel === 'string') {
    confirmLabelMessage = confirmLabel;
  } else {
    confirmLabelMessage = <FormattedMessage {...confirmLabel} />;
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <view.div
            data-testid="conversations-widget-full-drawer-confirm"
            css={{
              position: 'absolute',
              inset: 'space.0',
            }}
          />

          <view.div
            css={{
              backgroundColor: 'surface',
              opacity: 0.95,
              position: 'absolute',
              inset: 'space.0',
              zIndex: 10,
            }}
            uses={[
              animate.inout({
                duration: 150,
                opacity: 0,
                easing: 'ease-in-out',
              }),
            ]}
          >
            <view.div
              css={{
                stack: 'x',
                alignX: 'center',
                alignY: 'center',
                position: 'absolute',
                inset: 'space.0',
                paddingX: 'xlarge',
                paddingY: '10px',
              }}
            >
              <view.div
                css={{
                  stack: 'y',
                  gap: '10px',
                }}
              >
                <FocusScope autoFocus restoreFocus>
                  <view.div
                    css={{stack: 'y', gap: 'xxsmall', paddingBottom: 'xsmall'}}
                  >
                    <view.h2
                      data-testid="conversations-widget-confirmation-title"
                      css={{font: 'heading.small', marginBottom: 0}}
                    >
                      {titleMessage}
                    </view.h2>

                    <view.div
                      data-testid="conversations-widget-confirmation-description"
                      css={{font: 'body.small'}}
                    >
                      {descriptionMessage}
                    </view.div>
                  </view.div>
                  <view.div css={{stack: 'x', gap: 'small'}}>
                    <Button
                      data-testid="conversations-widget-confirmation-cancel"
                      type="secondary"
                      onPress={onCancel}
                    >
                      {cancelLabelMessage}
                    </Button>
                    <Button
                      data-testid="conversations-widget-confirmation-confirm"
                      type="destructive"
                      onPress={onConfirm}
                    >
                      {confirmLabelMessage}
                    </Button>
                  </view.div>
                </FocusScope>
              </view.div>
            </view.div>
          </view.div>
        </>
      )}
    </AnimatePresence>
  );
}
